import axios from 'axios'

import { type AnswerFeedback } from '@/lib/agent/answerFeedback'
import { type AgentEvent } from '../lib/agent/events'
import { followAgentTurn, type FollowTurnOptions, TurnStreamError } from '../lib/agent/stream'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

/**
 * Every call takes the agent's REST root as `basePath` — the staff chat and the
 * customer-facing Ask Cai panel are the same endpoints mounted twice, under
 * different permissions. `useAgentApi()` supplies it; see `AgentKindProvider`.
 */

export enum AgentConversationChannel {
  WEB = 'WEB',
  EMAIL = 'EMAIL',
}

export interface AgentConversation {
  id: string
  title: string
  dateCreated: string
  dateUpdated: string
  pendingConfirmation: boolean
  lastMessagePreview: string
  /** Display name of the staff member who created the chat. */
  createdByName: string
  /** Their headshot, else their legacy avatar; '' when they have neither. */
  createdByAvatar: string
  /** True when the current user created the chat. Lead chats are shared
   * read-only with other staff; only the owner can send/delete. */
  isOwner: boolean
  /** An EMAIL conversation is read here and answered by email, so it has no composer. */
  channel: AgentConversationChannel
  salesLeadId?: string | null
  organisationId?: string | null
}

export interface AgentConversationListResponse {
  count: number
  next: string | null
  previous: string | null
  results: AgentConversation[]
}

export function listAgentConversations(
  basePath: string,
  params?: {
    q?: string
    salesLead?: string
    organisation?: string
  }
): Promise<AgentConversation[]> {
  return axios.get(`${basePath}/`, { params: toSnakeCase(params) }).then((response) => {
    // Drf paginated response shape: { results: [...] } OR plain array if unpaginated.
    const data = response.data
    const results = Array.isArray(data) ? data : data.results
    return toCamelCase(results) as AgentConversation[]
  })
}

export function createAgentConversation(
  basePath: string,
  params?: {
    title?: string
    salesLead?: string
    organisation?: string
  }
): Promise<AgentConversation> {
  return axios
    .post(`${basePath}/`, toSnakeCase(params) ?? {})
    .then((response) => toCamelCase(response.data) as AgentConversation)
}

export function getAgentConversation(basePath: string, id: string): Promise<AgentConversation> {
  return axios.get(`${basePath}/${id}/`).then((response) => toCamelCase(response.data) as AgentConversation)
}

export function deleteAgentConversation(basePath: string, id: string): Promise<void> {
  return axios.delete(`${basePath}/${id}/`).then(() => undefined)
}

/**
 * Why the backend refused a decision, in its own words. It answers 409 both for
 * a turn already running (the card comes back, retry once it finishes) and for
 * an action already decided (it does not) — so the message has to come from the
 * server rather than be guessed from the status.
 */
export function decisionRefusalReason(error: unknown): string {
  const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null
  return typeof detail === 'string' && detail.trim() ? detail : 'Please try again.'
}

/**
 * Confirm or reject a pending action. Streams the resumed agent turn as SSE
 * just like /messages — same event consumer works for both.
 */
export async function decidePendingAction(
  basePath: string,
  conversationId: string,
  actionToken: string,
  decision: 'confirm' | 'reject'
): Promise<string | null> {
  const response = await axios.post(`${basePath}/${conversationId}/pending-actions/${actionToken}/`, {
    decision,
  })
  // Null until the last sibling in the group is decided — only then does the
  // backend have results for every deferred call and queue the resumed turn.
  return (response.data?.turn_id as string | null) ?? null
}

// ---- Chat items (history) -----------------------------------------------

export interface ChatItemAttachment {
  documentId: string
  attachmentId: string
  fileName: string
  fileExtension: string
  fileSize: number
  /**
   * Fresh signed URL — injected by the backend serializer at read time, not
   * baked into the persisted content. Absent on optimistic (just-sent) items.
   */
  fileUrl?: string | null
}

/**
 * A tool call the agent completed during a turn. Streamed live via
 * tool_call_start/tool_call_end, and persisted on the assistant item so the
 * cards survive the end of the stream and a page reload.
 */
export interface ChatToolCall {
  toolCallId: string
  tool: string
  label: string
  /** Kebab-case lucide icon chosen server-side; see `toolIcons.ts`. */
  icon: string
  argsSummary: string
  resultSummary: string | null
  error: string | null
  /**
   * Allow-listed fields from the tool's dict result (`extract_result_data` on
   * the backend), for tools whose card drives UI beyond a prose summary — today
   * only `fill_pdf_form`, whose document opens in the preview pane. Absent for
   * every other tool.
   */
  resultData?: { documentId?: string; fileName?: string } | null
}

export interface ChatItem {
  id: string
  /**
   * Frontend-generated UUID echoed back by the backend. Used to dedupe the
   * optimistic bubble against the persisted version once items refetches.
   * Absent on items persisted before client_id was wired through.
   */
  clientId?: string | null
  isFromUser: boolean
  content: {
    text?: string
    documentIds?: string[]
    attachments?: ChatItemAttachment[]
    /** Assistant items only; absent on turns that called no tools. */
    toolCalls?: ChatToolCall[]
  }
  dateCreated: string
  /** Staff only, on Cai's answers: the next three fields are absent for customers. */
  canRate?: boolean
  /** The viewing staff member's own rating, or null when they have not rated this answer. */
  feedback?: AnswerFeedback | null
  /** The answer's Langfuse trace, or null when the trace or the project ID is unknown. */
  traceUrl?: string | null
}

function answerFeedbackPath(chatItemId: string): string {
  return `/api/v1/insure/internal/agent-messages/${chatItemId}/feedback/`
}

export function saveAnswerFeedback(chatItemId: string, feedback: AnswerFeedback): Promise<AnswerFeedback> {
  return axios
    .put(answerFeedbackPath(chatItemId), toSnakeCase(feedback))
    .then((response) => toCamelCase(response.data) as AnswerFeedback)
}

export function clearAnswerFeedback(chatItemId: string): Promise<void> {
  return axios.delete(answerFeedbackPath(chatItemId)).then(() => undefined)
}

export function listChatItems(basePath: string, conversationId: string): Promise<ChatItem[]> {
  return axios
    .get(`${basePath}/${conversationId}/items/`)
    .then((response) => toCamelCase(response.data) as ChatItem[])
}

// ---- Page context -------------------------------------------------------

/**
 * What the user was looking at when they sent, as it goes over the wire.
 * Snake_case because it is passed through verbatim; the backend re-resolves the
 * entity itself and never trusts `label`.
 */
export interface PageContextPayload {
  page: string
  label: string
  path: string
  entity_type: string
  entity_id: string
  filters: Record<string, string>
}

export interface PageContextEntityLabel {
  label: string
  sublabel: string
}

// ---- Pending actions (server-persisted; survive navigation) -------------

export interface PendingActionDto {
  actionToken: string
  tool: string
  summary: string
  payload: Record<string, unknown>
  currentState: Record<string, unknown> | null
  provenance: 'user_authored' | 'tool_output'
  pendingIndex: number
  pendingTotal: number
}

export function listPendingActions(basePath: string, conversationId: string): Promise<PendingActionDto[]> {
  return axios
    .get(`${basePath}/${conversationId}/pending-actions/`)
    .then((response) => toCamelCase(response.data) as PendingActionDto[])
}

export interface RejectAllPendingActionsResult {
  groupsRejected: number
  actionsRejected: number
  hadErrors: boolean
  /** The queued acknowledgment turn to follow, or null when nothing resumed. */
  turnId: string | null
}

/** Reject every pending action on the conversation in one call. Plain JSON, no SSE. */
export function rejectAllPendingActions(
  basePath: string,
  conversationId: string
): Promise<RejectAllPendingActionsResult> {
  return axios
    .post(`${basePath}/${conversationId}/reject-all-pending-actions/`)
    .then((response) => toCamelCase(response.data) as RejectAllPendingActionsResult)
}

// ---- SSE events ---------------------------------------------------------

export type SseEvent = AgentEvent<{ text?: string }, { document_id?: string; file_name?: string }>

/** Submit one message and return the worker-owned turn ID. */
export async function sendAgentConversationMessage(
  basePath: string,
  conversationId: string,
  text: string,
  documentIds: string[],
  clientId: string,
  pageContext?: PageContextPayload | null
): Promise<string> {
  const response = await axios.post(`${basePath}/${conversationId}/messages/`, {
    text,
    document_ids: documentIds,
    client_id: clientId,
    page_context: pageContext ?? undefined,
  })
  return response.data.turn_id as string
}

/**
 * Name the record the current page is about, for the composer's context chip.
 * Pages with no record label themselves from the frontend route map and never
 * call this.
 */
export function resolvePageContextEntity(
  basePath: string,
  entityType: string,
  entityId: string
): Promise<PageContextEntityLabel> {
  return axios
    .get(`${basePath}/page-context/`, {
      params: { entity_type: entityType, entity_id: entityId },
    })
    .then((response) => toCamelCase(response.data) as PageContextEntityLabel)
}

/**
 * Follow a turn's event log over SSE, resuming from `fromSeq`.
 *
 * The turn runs on the worker, so this connection owns nothing — aborting it
 * stops watching, not the work. Reconnecting from seq 0 replays the turn so
 * far and then follows it live, which is what makes a reload survivable.
 */
export async function streamTurn(
  basePath: string,
  conversationId: string,
  turnId: string,
  onEvent: (event: SseEvent) => void,
  options: FollowTurnOptions = {}
): Promise<void> {
  await followAgentTurn(
    async (fromSeq, signal) => {
      try {
        const response = await axios.get(`${basePath}/${conversationId}/turns/${turnId}/stream/`, {
          headers: { Accept: 'text/event-stream, application/json, */*' },
          params: { from_seq: fromSeq },
          responseType: 'stream',
          adapter: 'fetch',
          signal,
        })
        return response.data as ReadableStream<Uint8Array>
      } catch (failure) {
        if (axios.isAxiosError(failure) && failure.response)
          throw new TurnStreamError(failure.response.status)
        throw failure
      }
    },
    onEvent,
    options
  )
}

/** Ask the worker to abandon a turn. A flag — the run is not on this request. */
export async function stopTurn(basePath: string, conversationId: string, turnId: string): Promise<void> {
  await axios.post(`${basePath}/${conversationId}/turns/${turnId}/stop/`, {})
}

/** The in-flight turn for this conversation, if any — asked on mount. */
export async function getActiveTurn(basePath: string, conversationId: string): Promise<string | null> {
  const response = await axios.get(`${basePath}/${conversationId}/active-turn/`)
  return (response.data?.turn_id as string | null) ?? null
}
